import {
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { tournamentsApi, type Tournament } from "@/api/tournaments";
import type { TournamentPagedResponse } from "@/shared/api/tournament";
import { QUERY_KEYS } from "@/hooks/query-keys";
import { QUERY_SPECIFIC_CONFIG } from "@/shared/config";

// useGetTournaments (kubb) не поддерживает query params в нужном виде,
// поэтому здесь остаётся tournamentsApi.
export function useTournaments(params?: {
  status?: string;
  game?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  page?: number;
  limit?: number;
}, options?: { enabled?: boolean }) {
  const queryKey = useMemo(() => QUERY_KEYS.tournaments(params), [params]);

  return useQuery<Tournament[]>({
    queryKey,
    queryFn: () => tournamentsApi.fetchAll(params),
    enabled: options?.enabled ?? true,
    staleTime: QUERY_SPECIFIC_CONFIG.TOURNAMENT_LIST.staleTime,
    gcTime: QUERY_SPECIFIC_CONFIG.TOURNAMENT_LIST.gcTime,
  });
}

export function useInfiniteTournaments(params?: {
  status?: string;
  game?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  limit?: number;
  enabled?: boolean;
}) {
  const queryKey = useMemo(
    () => QUERY_KEYS.tournamentsPaged(params ?? {}),
    [params],
  );
  const limit = params?.limit ?? 24;

  return useInfiniteQuery<TournamentPagedResponse>({
    queryKey,
    enabled: params?.enabled ?? true,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = Number(pageParam) || 1;
      return tournamentsApi.fetchPage({
        page,
        limit,
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.sortBy ? { sortBy: params.sortBy } : {}),
        ...(params?.sortDir ? { sortDir: params.sortDir } : {}),
      });
    },
    getNextPageParam: (lastPage) => {
      const page = lastPage as TournamentPagedResponse;
      return page.hasMore ? page.page + 1 : undefined;
    },
    staleTime: QUERY_SPECIFIC_CONFIG.TOURNAMENT_LIST.staleTime,
    gcTime: QUERY_SPECIFIC_CONFIG.TOURNAMENT_LIST.gcTime,
  });
}

